import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Building2, Plus, MoreHorizontal, Search } from 'lucide-react'
import { adminApi } from '@/lib/api'
import { useOrgContext } from '@/contexts/OrgContext'
import { OrgRecord } from '@/types/organization'

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const RESERVED_SLUGS = new Set([
  'api', 'app', 'www', 'web', 'admin', 'portal',
  'staging', 'development', 'dev', 'production', 'prod', 'test', 'testing', 'sandbox', 'preview', 'demo',
  'cdn', 'assets', 'static', 'media', 'images', 'files', 'uploads', 'storage', 'drive',
  'mail', 'email', 'smtp', 'imap',
  'ws', 'websocket', 'socket', 'realtime',
  'auth', 'login', 'sso', 'oauth', 'identity',
  'metrics', 'monitoring', 'status', 'health', 'healthcheck',
  'logs', 'logging', 'debug', 'trace',
  'docs', 'documentation', 'help', 'support', 'faq', 'wiki', 'blog', 'news',
  'kb', 'knowledge', 'knowledgebase',
  'account', 'accounts', 'billing', 'payments', 'checkout', 'subscribe',
  'settings', 'config', 'configure', 'preferences',
  'dashboard', 'console', 'panel',
  'internal', 'system', 'platform', 'global', 'public', 'private',
  'root', 'master', 'main',
  'ponder', 'botto', 'ponderdo',
  'graphql', 'rest', 'rpc', 'grpc', 'proxy', 'gateway', 'edge',
  'ci', 'cd', 'build', 'deploy', 'release',
  'git', 'repo', 'registry', 'npm', 'packages',
  'calendar', 'chat', 'meet', 'video', 'stream',
  'search', 'explore', 'discover', 'marketplace', 'store', 'shop',
  'connect', 'bridge', 'connector', 'integration', 'integrations',
  'workflows', 'workflow', 'automation',
  'agents', 'agent', 'ai', 'bot', 'bots',
])

function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug.toLowerCase())
}

const statusBadge = (status: string) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
    case 'suspended':
      return <Badge variant="destructive">Suspended</Badge>
    case 'provisioning':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Provisioning</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

export function OrganizationsPage() {
  const navigate = useNavigate()
  const { setSelectedOrg } = useOrgContext()
  const [orgs, setOrgs] = useState<OrgRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', plan: 'free', adminEmails: '' })

  const fetchOrgs = async () => {
    try {
      setLoading(true)
      const data = await adminApi.getOrganizations()
      setOrgs(data.orgs || [])
    } catch (err) {
      console.error('Failed to fetch organizations:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchOrgs() }, [])

  const handleCreate = async () => {
    if (!newOrg.name || !newOrg.slug) return
    setCreating(true)
    try {
      const emails = newOrg.adminEmails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(Boolean)
      await adminApi.createOrganization({
        name: newOrg.name,
        slug: newOrg.slug,
        plan: newOrg.plan,
        initialAdminEmails: emails.length > 0 ? emails : undefined,
      })
      setCreateOpen(false)
      setNewOrg({ name: '', slug: '', plan: 'free', adminEmails: '' })
      fetchOrgs()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create organization')
    } finally {
      setCreating(false)
    }
  }

  const handleStatusChange = async (org: OrgRecord, status: string) => {
    try {
      await adminApi.updateOrganization(org.slug, { status })
      fetchOrgs()
    } catch (err) {
      console.error('Failed to update org status:', err)
    }
  }

  const handleRowClick = (org: OrgRecord) => {
    setSelectedOrg(org)
    navigate('/org-settings')
  }

  const filtered = orgs.filter(o =>
    !search || o.name.toLowerCase().includes(search.toLowerCase()) || o.slug.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Organizations</h1>
            <p className="text-sm text-muted-foreground">{orgs.length} organizations</p>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Organization
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No organizations found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(org => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(org)}
                  >
                    <TableCell className="font-mono text-sm">{org.slug}</TableCell>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{org.plan || 'free'}</Badge>
                    </TableCell>
                    <TableCell>{org.tier || 'shared'}</TableCell>
                    <TableCell>{statusBadge(org.status)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => handleRowClick(org)}>
                            Edit Settings
                          </DropdownMenuItem>
                          {org.status === 'active' ? (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleStatusChange(org, 'suspended')}
                            >
                              Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleStatusChange(org, 'active')}>
                              Unsuspend
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Set up a new organization with its own isolated data schema.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="My Organization"
                value={newOrg.name}
                onChange={e => {
                  const name = e.target.value
                  setNewOrg(prev => ({ ...prev, name, slug: slugify(name) }))
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input
                placeholder="my-organization"
                value={newOrg.slug}
                onChange={e => setNewOrg(prev => ({ ...prev, slug: e.target.value }))}
                className={isReservedSlug(newOrg.slug) ? 'border-destructive' : ''}
              />
              {isReservedSlug(newOrg.slug) ? (
                <p className="text-xs text-destructive">"{newOrg.slug}" is a reserved name and cannot be used.</p>
              ) : (
                <p className="text-xs text-muted-foreground">Used in URLs and schema names. Lowercase, hyphens only.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={newOrg.plan} onValueChange={v => setNewOrg(prev => ({ ...prev, plan: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Initial Admin Emails</Label>
              <Textarea
                placeholder="admin@example.com&#10;admin2@example.com"
                value={newOrg.adminEmails}
                onChange={e => setNewOrg(prev => ({ ...prev, adminEmails: e.target.value }))}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">One email per line or comma-separated. These users will be added as Admins.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newOrg.name || !newOrg.slug || isReservedSlug(newOrg.slug)}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
